import React, { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "../Includes/Sidebar";
import Footer from "../Includes/Footer";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import GeneralService from "../../services/general.service";

export default function ViewOpenHours() {
    const [openHours, setOpenHours] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const state = useSelector((state) => state.stateVals);
    const { id: adminId } = state;

    // Days of week mapping for better display
    const dayNames = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday',
        'mon': 'Monday',
        'tue': 'Tuesday',
        'wed': 'Wednesday',
        'thu': 'Thursday',
        'fri': 'Friday',
        'sat': 'Saturday',
        'sun': 'Sunday',
        '1': 'Monday',
        '2': 'Tuesday',
        '3': 'Wednesday',
        '4': 'Thursday',
        '5': 'Friday',
        '6': 'Saturday',
        '0': 'Sunday',
        '7': 'Sunday'
    };

    // Function to get proper day name
    const getDayName = (day) => {
        
        const dayStr = day.toString().toLowerCase();
        return dayNames[dayStr] || day.toString();
    };

    // Fetch open hours data
    const fetchOpenHours = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await GeneralService.listOpenHours();
            console.log("API Response:", response.data);
            
            let hoursData = [];
            if (response.data && Array.isArray(response.data)) {
                hoursData = response.data;
            } else if (response.data && response.data.hours && Array.isArray(response.data.hours)) {
                hoursData = response.data.hours;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                hoursData = response.data.data;
            }
            
            // Ensure each hours entry has required properties
            const sanitizedHours = hoursData.map(hour => ({
                id: hour.id || Math.random().toString(36).substr(2, 9),
                day: hour.day || hour.day_of_week || hour.weekday || '',
                open_time: hour.open_time || hour.opening_time || hour.start_time || '',
                close_time: hour.close_time || hour.closing_time || hour.end_time || '',
                is_closed: hour.is_closed || hour.closed || false,
                emergency_only: hour.emergency_only || hour.emergency || false,
                is_active: hour.is_active !== undefined ? hour.is_active : true,
                created_at: hour.created_at || new Date().toISOString(),
                updated_at: hour.updated_at || new Date().toISOString(),
                ...hour
            }));
            
            console.log("Sanitized hours data:", sanitizedHours);
            setOpenHours(sanitizedHours);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch open hours";
            setError(errorMessage);
            console.error("Fetch open hours error:", err);
            setOpenHours([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOpenHours();
    }, [fetchOpenHours]);

    // Enhanced delete function with better error handling
    const removeOpenHour = async (id, day) => {
        const dayDisplayName = getDayName(day);
        
        const result = await Swal.fire({
            title: "Delete Open Hours",
            html: `Are you sure you want to permanently delete <strong>${dayDisplayName}</strong> hours?<br><small class="text-muted">This action cannot be undone.</small>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete!",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                confirmButton: 'btn btn-danger',
                cancelButton: 'btn btn-secondary'
            }
        });

        if (!result.isConfirmed) return;

        try {
            // Show loading indicator
            Swal.fire({
                title: 'Deleting...',
                text: 'Please wait while we delete the open hours.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await GeneralService.deleteOpenHoursById(id);
            console.log("Delete response:", response);
            
            // Check various response patterns for success
            const isSuccess = response && (
                response.success === true || 
                response.status === 'success' || 
                response.status === 200 ||
                response.statusText === 'OK' ||
                (response.data && (response.data.success === true || response.data.status === 'success'))
            );
            
            if (isSuccess) {
                // Update local state
                setOpenHours(prevHours => prevHours.filter(hour => hour.id !== id));
                
                // Reset pagination if needed
                const updatedHoursCount = openHours.filter(h => h.id !== id).length;
                const newTotalPages = Math.ceil(updatedHoursCount / recordsPerPage);
                if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(1);
                }
                
                Swal.fire({
                    title: "Deleted!",
                    text: `${dayDisplayName} hours have been removed successfully.`,
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false,
                    timerProgressBar: true
                });
            } else {
                throw new Error(response?.message || response?.data?.message || 'Delete operation failed');
            }
        } catch (err) {
            console.error("Delete open hours error:", err);
            
            // Fallback: refresh data to check if deletion actually worked
            try {
                await fetchOpenHours();
                const hourStillExists = openHours.some(h => h.id === id);
                if (!hourStillExists) {
                    Swal.fire({
                        title: "Deleted!",
                        text: `${dayDisplayName} hours have been removed successfully.`,
                        icon: "success",
                        timer: 3000,
                        showConfirmButton: false
                    });
                    return;
                }
            } catch (refreshError) {
                console.error("Failed to refresh after delete error:", refreshError);
            }
            
            Swal.fire({
                title: "Error!",
                text: err.response?.data?.message || err.message || "Failed to delete open hours. Please try again.",
                icon: "error",
                confirmButtonText: "OK"
            });
        }
    };

    // Debounced search with better performance
    const debouncedSearch = useMemo(
        () => debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 300),
        []
    );

    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Sorting functionality
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Enhanced filtering and sorting
    const processedOpenHours = useMemo(() => {
        let result = Array.isArray(openHours) ? [...openHours] : [];
        
        // Filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter((hour) => {
                if (!hour) return false;
                
                const dayName = getDayName(hour.day);
                const searchableFields = [
                    hour.day,
                    dayName,
                    hour.open_time,
                    hour.close_time,
                    hour.is_closed ? 'closed' : 'open',
                    hour.emergency_only ? 'emergency' : '',
                    hour.is_active ? 'active' : 'inactive'
                ];
                
                return searchableFields.some(field => 
                    field && field.toString().toLowerCase().includes(query)
                );
            });
        }
        
        // Sort
        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key] || '';
                let bValue = b[sortConfig.key] || '';
                
                // Handle time values
                if (sortConfig.key === 'open_time' || sortConfig.key === 'close_time') {
                    aValue = aValue.toString();
                    bValue = bValue.toString();
                } else if (sortConfig.key === 'day') {
                    // Sort days by weekday order
                    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    const aDayIndex = dayOrder.indexOf(aValue.toString().toLowerCase());
                    const bDayIndex = dayOrder.indexOf(bValue.toString().toLowerCase());
                    aValue = aDayIndex >= 0 ? aDayIndex : 999;
                    bValue = bDayIndex >= 0 ? bDayIndex : 999;
                } else {
                    aValue = aValue.toString().toLowerCase();
                    bValue = bValue.toString().toLowerCase();
                }
                
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        return result;
    }, [openHours, searchQuery, sortConfig]);

    // Pagination calculations
    const totalRecords = processedOpenHours.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const displayedOpenHours = processedOpenHours.slice(startIndex, startIndex + recordsPerPage);

    // Refresh handler
    const handleRefresh = () => {
        setSearchQuery("");
        setCurrentPage(1);
        setSortConfig({ key: null, direction: 'asc' });
        fetchOpenHours();
    };

    // Export functionality
    const handleExport = () => {
        const csvContent = [
            ['Day', 'Open Time', 'Close Time', 'Status', 'Emergency Only', 'Active'],
            ...processedOpenHours.map(hour => [
                getDayName(hour.day),
                hour.open_time,
                hour.close_time,
                hour.is_closed ? 'Closed' : 'Open',
                hour.emergency_only ? 'Yes' : 'No',
                hour.is_active ? 'Yes' : 'No'
            ])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `open_hours_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            // Handle both HH:MM:SS and HH:MM formats
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours);
            const minute = minutes || '00';
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}:${minute} ${ampm}`;
        } catch (e) {
            return timeString;
        }
    };

    // Sort indicator component
    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) {
            return <span className="sort-indicator">⇅</span>;
        }
        return (
            <span className="sort-indicator active">
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        {/* Header */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="mb-0">
                                Open Hours Management 
                                <span className="number-records ms-2">({totalRecords} total)</span>
                            </h5>
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    title="Refresh data"
                                >
                                    {loading ? '🔄 Loading...' : '🔄 Refresh'}
                                </button>
                                <Link 
                                    className="btn btn-primary btn-sm"
                                    to="/add-hour"
                                    title="Add new open hours"
                                >
                                    ➕ Add Hours
                                </Link>
                            </div>
                        </div>

                        {/* Debug Info - Remove in production */}
                        {/* {process.env.NODE_ENV === 'development' && openHours.length > 0 && (
                            <div className="alert alert-info mb-3">
                                <strong>Debug Info:</strong> 
                                <pre>{JSON.stringify(openHours[0], null, 2)}</pre>
                            </div>
                        )} */}

                        {/* Controls */}
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="form-label small">Records per page:</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={recordsPerPage}
                                    onChange={(e) => {
                                        setRecordsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={5}>5 per page</option>
                                    <option value={10}>10 per page</option>
                                    <option value={25}>25 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            </div>
                            <div className="col-md-8">
                                <label className="form-label small">Search open hours:</label>
                                <div className="input-group">
                                    <span className="input-group-text">🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Search by day, time, status..."
                                        className="form-control"
                                        onChange={handleSearch}
                                    />
                                    {searchQuery && (
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setSearchQuery("");
                                                setCurrentPage(1);
                                            }}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="bg-gred">
                                    <tr className="text-white">
                                        <th>#</th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('day')}
                                            role="button"
                                            tabIndex={0}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Day <SortIndicator column="day" />
                                        </th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('open_time')}
                                            role="button"
                                            tabIndex={0}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Open Time <SortIndicator column="open_time" />
                                        </th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('close_time')}
                                            role="button"
                                            tabIndex={0}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Close Time <SortIndicator column="close_time" />
                                        </th>
                                        <th>Status</th>
                                        <th>Emergency Only</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <div className="mt-2">Loading open hours data...</div>
                                            </td>
                                        </tr>
                                    ) : error ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="alert alert-danger mb-0">
                                                    <h6>⚠️ Error Loading Data</h6>
                                                    <p className="mb-2">{error}</p>
                                                    <button 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={handleRefresh}
                                                    >
                                                        Try Again
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : displayedOpenHours.length > 0 ? (
                                        displayedOpenHours.map((hour, index) => {
                                            const dayDisplayName = getDayName(hour.day);
                                            return (
                                                <tr key={`hour-${hour.id}`} className="align-middle">
                                                    <td className="fw-bold">{startIndex + index + 1}</td>
                                                    <td className="fw-semibold">
                                                        <span className="badge bg-primary">
                                                            {dayDisplayName}
                                                        </span>
                                                        {process.env.NODE_ENV === 'development' && (
                                                            <small className="d-block text-muted mt-1">
                                                                {hour.day_range}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td className="text-success fw-bold">
                                                        {formatTime(hour.open_time)}
                                                    </td>
                                                    <td className="text-danger fw-bold">
                                                        {formatTime(hour.close_time)}
                                                    </td>
                                                    <td>
                                                        {hour.is_closed ? (
                                                            <span className="badge bg-danger">Closed</span>
                                                        ) : (
                                                            <span className="badge bg-success">Open</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {hour.emergency_only ? (
                                                            <span className="badge bg-warning text-dark">Emergency Only</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">Regular</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {hour.is_active ? (
                                                            <span className="badge bg-success">Active</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">Inactive</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="btn-group-vertical gap-1">
                                                            <button 
                                                                className="btn btn-sm btn-danger" 
                                                                onClick={() => removeOpenHour(hour.id, hour.day)}
                                                                title="Delete open hours"
                                                            >
                                                                🗑️ Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <div className="text-muted">
                                                    <h5>🕒 No Open Hours Found</h5>
                                                    <p>
                                                        {searchQuery 
                                                            ? `No open hours match "${searchQuery}"` 
                                                            : 'No open hours have been configured yet'
                                                        }
                                                    </p>
                                                    {searchQuery && (
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => {
                                                                setSearchQuery("");
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            Clear Search
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Enhanced Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted small">
                                    Showing {startIndex + 1} to {Math.min(startIndex + recordsPerPage, totalRecords)} of {totalRecords} entries
                                </div>
                                <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                            >
                                                First
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </button>
                                        </li>
                                        
                                        {/* Page numbers with smart truncation */}
                                        {(() => {
                                            const pages = [];
                                            const maxVisiblePages = 5;
                                            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                            
                                            if (endPage - startPage + 1 < maxVisiblePages) {
                                                startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                            }
                                            
                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(
                                                    <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                        <button 
                                                            className="page-link" 
                                                            onClick={() => setCurrentPage(i)}
                                                        >
                                                            {i}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                            return pages;
                                        })()}
                                        
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Last
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
                <Footer />
            </div>

            {/* Enhanced CSS */}
            <style jsx>{`
                .content {
                    min-height: 100vh;
                    background-color: #f8f9fa;
                }
                
                .table-responsive {
                    border-radius: 0.5rem;
                    box-shadow: 0 0 0 1px rgba(0,0,0,0.05);
                }
                
                .table {
                    margin-bottom: 0;
                    background-color: white;
                }
                
                .table th {
                    font-weight: 600;
                    font-size: 13px;
                    border-bottom: 2px solid #dee2e6;
                    position: relative;
                    user-select: none;
                }
                
                .sortable-header {
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                }
                
                .sortable-header:hover {
                    background-color: rgba(255,255,255,0.1) !important;
                }
                
                .sort-indicator {
                    margin-left: 5px;
                    opacity: 0.5;
                    font-size: 12px;
                }
                
                .sort-indicator.active {
                    opacity: 1;
                    color: #fff;
                }
                
                .table td {
                    font-size: 13px;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f3f4;
                }
                
                .table tbody tr:hover {
                    background-color: #f8f9fa;
                }
                
                .number-records {
                    color: #f44c00;
                    font-weight: bold;
                    font-size: 0.9em;
                }
                
                .bg-gred {
                    background: linear-gradient(135deg, #f44c00 0%, #ff6b35 100%) !important;
                }
                
                .bg-gred th {
                    color: white !important;
                    border-bottom-color: rgba(255,255,255,0.2) !important;
                }
                
                .btn-group-vertical .btn {
                    font-size: 11px;
                    padding: 0.25rem 0.5rem;
                }
                
                .btn-primary {
                    background-color: #007bff;
                    border-color: #007bff;
                }
                
                .btn-primary:hover {
                    background-color: #0056b3;
                    border-color: #0056b3;
                }
                
                .btn-danger {
                    background-color: #dc3545;
                    border-color: #dc3545;
                }
                
                .btn-danger:hover {
                    background-color: #c82333;
                    border-color: #bd2130;
                }
                
                .pagination .page-item.active .page-link {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    color: white;
                }
                
                .pagination .page-link {
                    color: #f44c00;
                    border-color: #dee2e6;
                }
                
                .pagination .page-link:hover:not(.disabled) {
                    color: #d43f00;
                    background-color: #f8f9fa;
                    border-color: #f44c00;
                }
                
                .pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    cursor: not-allowed;
                }
                
                .badge {
                    font-size: 0.75em;
                }
                
                .text-truncate {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                .input-group-text {
                    background-color: #f8f9fa;
                    border-color: #ced4da;
                }
                
                .alert {
                    border: none;
                    border-radius: 0.5rem;
                }
                
                .spinner-border {
                    width: 2rem;
                    height: 2rem;
                }
                
                @media (max-width: 768px) {
                    .table-responsive {
                        font-size: 12px;
                    }
                    
                    .btn-group-vertical .btn {
                        font-size: 10px;
                        padding: 0.2rem 0.4rem;
                    }
                }
            `}</style>
        </>
    );
}